import { useCallback, useEffect, useState } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import _ from 'lodash';

import useDeviceScroll from 'src/hooks/useDeviceScroll';
import useMutationObservable from 'src/hooks/useMutationObservable';
import useIsMobile from 'src/hooks/useIsMobile';

interface MatchPath {
  params: {
    meetingCode: string,
    event: string
  }
}

export default function WindowScrollHandler() {
  const [pagesScroll, setPagesScroll] = useState({});
  const location = useLocation();
  // We want this so we know that we are accessing to race detail, and we want to have access to params
  const racingMatch:MatchPath = matchPath(location.pathname, {
    path: ['/racing/:meetingCode/:event', '/racing/:meetingCode/:event/:tab', '/racing/:meetingCode/:event/:tab/:market'],
    exact: true,
    strict: true
  });
  const scrollPosition = useDeviceScroll();
  const isMobile = useIsMobile();
  const [path, setPath] = useState('');

  // Checking when body classes mutate, so we scroll back to previous when position when class is removed.
  const bodyMutated = useCallback((mutationList: MutationRecord[]) => {
    if (isMobile) {
      for (const mutation of mutationList) {
        if (mutation.type === 'attributes' && (mutation.attributeName === 'class' || mutation.attributeName === 'style')) {
          if (!document.body.classList.contains('disable-scroll') && document.body.style.overflow !== 'hidden') {
            window.scrollTo(0, pagesScroll[path]);
          }
        }
      }
    }
  }, [pagesScroll, location.pathname, isMobile]);
  useMutationObservable(document.body, bodyMutated);

  // Update scrollPosition for current page.
  useEffect(() => {
    if (!document.body.classList.contains('disable-scroll') && document.body.style.overflow !== 'hidden') {
      setPagesScroll(state => {
        const simplifiedPath = location.pathname.includes('/racing') && racingMatch
          ? `/racing/${ racingMatch.params.meetingCode }/${ racingMatch.params.event }`
          : location.pathname;
        return { ...state, [`${ simplifiedPath }`]: (`${ simplifiedPath }` in state) ? scrollPosition : 0 };
      });
    }
  }, [scrollPosition]);

  // Remove pages from other products.
  useEffect(() => {
    setPath(location.pathname);
    const state = _.reduce(JSON.parse(JSON.stringify(pagesScroll)), (result, value, key) => {
      if (location.pathname.includes('/racing') && key.includes('/racing')) {
        let simplifiedPath = key;

        if (racingMatch) {
          const { meetingCode, event } = racingMatch.params;
          simplifiedPath = `/racing/${ meetingCode }/${ event }`;
        }
        setPath(`${ simplifiedPath }`);
        return { ...result, [`${ simplifiedPath }`]: value };
      } else if (location.pathname.includes('/lotto') && key.includes('/lotto')) {
        return { ...result, [key]: value };
      } else if (location.pathname.includes('/sports-betting') && key.includes('/sports-betting')) {
        return { ...result, [key]: value };
      }

      return {};
    }, {});

    setPagesScroll(state);
    window.scrollTo(0, pagesScroll[path] || 0);
  }, [location.pathname]);

  return null;
}
